import React, { useEffect, useState } from "react";
import api, { formatError, API } from "@/lib/api";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X, Save, Download, ArrowLeft, Users, Trash2, Search } from "lucide-react";

export default function TeamDetail() {
  const { teamId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [team, setTeam] = useState(null);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/teams/${teamId}`).then(r => setTeam(r.data)).catch(() => nav("/teams"));
  }, [teamId, nav]);

  if (!user) return <Link to="/login">Login eerst</Link>;
  if (!team) return <div className="text-slate-500">Laden...</div>;

  const set = (patch) => setTeam({ ...team, ...patch });

  const save = async () => {
    setSaving(true); setMsg("");
    try { await api.put(`/teams/${teamId}`, team); setMsg("Opgeslagen ✓"); setTimeout(() => setMsg(""), 2000); }
    catch (e) { setMsg(formatError(e)); }
    finally { setSaving(false); }
  };

  const addGezSprong = (sprong) => set({ gezamenlijke_sprongen: [...team.gezamenlijke_sprongen, sprong] });
  const removeGezSprong = (i) => set({ gezamenlijke_sprongen: team.gezamenlijke_sprongen.filter((_, idx) => idx !== i) });

  const addTurner = () => set({ turners: [...team.turners, { naam: "Nieuwe turner", geboortejaar: null, keuze_sprongen: [] }] });
  const updateTurner = (i, patch) => set({ turners: team.turners.map((t, idx) => idx === i ? { ...t, ...patch } : t) });
  const removeTurner = (i) => set({ turners: team.turners.filter((_, idx) => idx !== i) });

  const addTurnerSprong = (turnerIdx, sprong) => updateTurner(turnerIdx, { keuze_sprongen: [...team.turners[turnerIdx].keuze_sprongen, sprong] });
  const removeTurnerSprong = (turnerIdx, sprongIdx) => updateTurner(turnerIdx, { keuze_sprongen: team.turners[turnerIdx].keuze_sprongen.filter((_, idx) => idx !== sprongIdx) });

  const downloadExcelForTurner = async (turner) => {
    const allSprongen = [...team.gezamenlijke_sprongen, ...turner.keuze_sprongen];
    const tplMap = { mt: "groepspringen_6_sprongen", pegasus: "groepspringen_6_sprongen", airtrack: "groepspringen_airtrack" };
    const tpl = team.aantal_gezamenlijk === 8 ? "groepspringen_8_sprongen" : tplMap[team.toestel] || "groepspringen_6_sprongen";
    const r = await api.post("/excel/generate", { template: tpl, vereniging: team.vereniging, team: turner.naam, niveau: team.niveau, leeftijd: team.leeftijd, sprongen: allSprongen }, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `${team.team_naam}_${turner.naam}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  const gezTotalD = team.gezamenlijke_sprongen.reduce((s, x) => s + (x.d || 0), 0).toFixed(1);

  return (
    <div data-testid="team-detail-page">
      <Link to="/teams" className="text-sm text-orange-600 hover:underline inline-flex items-center gap-1 mb-3" data-testid="back-to-teams"><ArrowLeft size={14}/> Terug naar teams</Link>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">Team</div>
          <h1 className="heading-display text-3xl sm:text-4xl mt-1">{team.team_naam}</h1>
          <div className="text-sm text-slate-600 mt-1">{team.vereniging} · {team.leeftijd} · niveau {team.niveau} · {team.toestel}</div>
        </div>
        <div className="flex items-center gap-2">
          {msg && <span className="text-sm text-emerald-600 font-medium" data-testid="save-msg">{msg}</span>}
          <button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50" data-testid="team-save-btn">
            <Save size={16}/> {saving ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>

      {/* Gezamenlijke sprongen */}
      <section className="gs-card p-5 mb-6" data-testid="gezamenlijke-sectie">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-lg">Gezamenlijke sprongen ({team.gezamenlijke_sprongen.length}/{team.aantal_gezamenlijk})</h2>
          <span className="text-sm text-slate-600">Totaal D: <span className="font-mono font-bold">{gezTotalD}</span></span>
        </div>
        <SprongPicker toestel={team.toestel} onAdd={addGezSprong}/>
        <ul className="mt-3 space-y-2">
          {team.gezamenlijke_sprongen.map((s, i) => (
            <li key={i} className="flex items-center gap-2 bg-slate-50 rounded px-3 py-2" data-testid={`gez-sprong-${i}`}>
              <span className="flex-1 font-medium">{s.naam}</span>
              <span className="font-mono">{s.d?.toFixed?.(1) ?? s.d}</span>
              <button onClick={() => removeGezSprong(i)} className="text-slate-400 hover:text-red-600"><X size={16}/></button>
            </li>
          ))}
          {team.gezamenlijke_sprongen.length === 0 && <li className="text-slate-500 text-sm text-center py-4">Voeg gezamenlijke sprongen toe.</li>}
        </ul>
      </section>

      {/* Turners */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-lg">Turners ({team.turners.length})</h2>
          <button onClick={addTurner} className="text-sm bg-secondary text-white px-3 py-1.5 rounded font-semibold inline-flex items-center gap-1" data-testid="add-turner-btn"><Plus size={14}/> Turner toevoegen</button>
        </div>
        {team.turners.length === 0 && <div className="gs-card p-6 text-center text-slate-500" data-testid="turners-empty">Voeg turners toe en hun 2 keuzesprongen.</div>}
        <div className="space-y-3">
          {team.turners.map((turner, ti) => {
            const turnerD = turner.keuze_sprongen.reduce((s, x) => s + (x.d || 0), 0);
            const totalD = (turner.keuze_sprongen.reduce((s, x) => s + (x.d || 0), 0) + team.gezamenlijke_sprongen.reduce((s, x) => s + (x.d || 0), 0)).toFixed(1);
            return (
              <div key={ti} className="gs-card p-4" data-testid={`turner-${ti}`}>
                <div className="flex items-center gap-2 mb-3">
                  <input value={turner.naam} onChange={e => updateTurner(ti, { naam: e.target.value })} className="font-bold px-2 py-1 border rounded flex-1" placeholder="Turner naam" data-testid={`turner-naam-${ti}`}/>
                  <input type="number" placeholder="Geb.jr" value={turner.geboortejaar || ""} onChange={e => updateTurner(ti, { geboortejaar: e.target.value ? parseInt(e.target.value) : null })} className="w-20 px-2 py-1 border rounded" data-testid={`turner-jr-${ti}`}/>
                  <span className="text-sm text-slate-600">Totaal D: <span className="font-mono font-bold">{totalD}</span></span>
                  <button onClick={() => downloadExcelForTurner(turner)} className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded font-semibold inline-flex items-center gap-1" data-testid={`turner-excel-${ti}`}><Download size={14}/> Excel</button>
                  <button onClick={() => removeTurner(ti)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`remove-turner-${ti}`}><Trash2 size={16}/></button>
                </div>
                <div className="text-xs text-slate-500 mb-2">Keuze sprongen ({turner.keuze_sprongen.length}/2):</div>
                <SprongPicker toestel={team.toestel} onAdd={(s) => addTurnerSprong(ti, s)} compact/>
                <ul className="mt-2 space-y-1">
                  {turner.keuze_sprongen.map((s, si) => (
                    <li key={si} className="flex items-center gap-2 bg-slate-50 rounded px-2 py-1.5 text-sm" data-testid={`turner-sprong-${ti}-${si}`}>
                      <span className="flex-1">{s.naam}</span>
                      <span className="font-mono">{s.d?.toFixed?.(1) ?? s.d}</span>
                      <button onClick={() => removeTurnerSprong(ti, si)} className="text-slate-400 hover:text-red-600"><X size={14}/></button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function SprongPicker({ toestel, onAdd, compact }) {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState({ naam: "", d: "" });

  useEffect(() => {
    if (!q.trim()) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get("/checker/suggest", { params: { query: q, toestel, limit: 8 } }); setSuggestions(r.data); }
      catch { setSuggestions([]); }
    }, 150);
    return () => clearTimeout(t);
  }, [q, toestel]);

  const pick = (s) => { onAdd({ naam: s.naam, d: s.d }); setQ(""); setSuggestions([]); setOpen(false); };
  const submitCustom = (e) => {
    e.preventDefault();
    if (!custom.naam || !custom.d) return;
    onAdd({ naam: custom.naam + " (eigen)", d: parseFloat(custom.d) });
    setCustom({ naam: "", d: "" }); setShowCustom(false);
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
            placeholder={compact ? "Zoek sprong..." : "Voeg sprong toe (bv. 'dub hoek')"}
            className={`w-full pl-9 pr-3 ${compact ? "py-1.5 text-sm" : "py-2.5"} border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500`}/>
        </div>
        <button type="button" onClick={() => setShowCustom(!showCustom)} className={`text-orange-600 border border-orange-300 hover:bg-orange-50 px-3 ${compact ? "py-1.5 text-xs" : "py-2.5 text-sm"} rounded-lg font-semibold whitespace-nowrap`} data-testid="own-sprong-btn">
          + Eigen
        </button>
      </div>
      {open && suggestions.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((s) => (
            <li key={s.naam} onMouseDown={(e) => { e.preventDefault(); pick(s); }} className="flex justify-between px-4 py-2 cursor-pointer hover:bg-orange-50 border-b last:border-0">
              <span>{s.naam}</span><span className="font-mono text-sm text-slate-500">{s.d?.toFixed(1)}</span>
            </li>
          ))}
        </ul>
      )}
      {showCustom && (
        <form onSubmit={submitCustom} className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2">
          <input required placeholder="Sprong naam" value={custom.naam} onChange={e => setCustom({...custom, naam: e.target.value})} className="flex-1 px-2 py-1.5 border rounded text-sm"/>
          <input required type="number" step="0.1" placeholder="D" value={custom.d} onChange={e => setCustom({...custom, d: e.target.value})} className="w-20 px-2 py-1.5 border rounded text-sm"/>
          <button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded text-sm font-semibold">OK</button>
        </form>
      )}
    </div>
  );
}
